/* eslint-disable react/forbid-dom-props */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Zap, AlertTriangle } from 'lucide-react';
import { casesApi } from '../../services/api';

// Fallback para respostas antigas, sem `missing_fields`. Descreve só o que o gate realmente
// exige: a versão anterior listava RG, endereço e telefone no grupo do reclamante, campos que
// não bloqueiam nada e mandavam o atendente procurar o problema no lugar errado.
const GROUP_LABELS = {
  comarca: 'Comarca',
  reclamante: 'Reclamante: nome, CPF e gênero',
  reclamado: 'Reclamado: nome do primeiro reclamado',
  contrato: 'Contrato: admissão, função e salário registrado ou extrafolha',
  jornada: 'Jornada: início, término e tipo de controle de ponto',
  contradicao: 'Contradição na ficha: pedido marcado contra a ordem escrita ou contra o fato que o sustenta',
  pedidos: 'Pedidos: nenhum pedido marcado na aba Pedidos',
  rescisao: 'Rescisão: tipo de desligamento e data de término incoerentes (aba Contrato)',
  prescricao: 'Prescrição: biênio vencido mesmo com a projeção do aviso prévio (aba Contrato, Prescrição)',
};

export default function ConfirmGenerationModal({ caseId, onClose, onConfirm, submitting }) {
  const { data: checkData, isLoading, error } = useQuery({
    queryKey: ['pre-generation-check', caseId],
    queryFn: () => casesApi.getPreGenerationCheck(caseId),
    enabled: !!caseId,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const formatBRL = (val) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: '1.125rem', display: 'flex', alignItems: 'center', gap: '.4rem' }}>
            <Zap size={18} className="text-gold" /> Confirmar Geração de Petição
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: '.2rem .4rem' }}>
            <X size={16} />
          </button>
        </div>

        <div className="modal-body">
          {isLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem 0', gap: '.75rem' }}>
              <div className="spinner" style={{ width: 24, height: 24 }} />
              <span style={{ fontSize: '.875rem', color: 'var(--text-muted)' }}>
                Verificando completude dos dados da ficha...
              </span>
            </div>
          )}

          {error && (
            <div className="alert alert-error" style={{ fontSize: '.875rem' }}>
              <AlertTriangle size={14} />
              <span>Erro ao realizar verificação pré-geração. Tente salvar o rascunho primeiro.</span>
            </div>
          )}

          {checkData && !checkData.passed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="alert alert-error" style={{ margin: 0, padding: '.75rem 1rem' }}>
                <AlertTriangle size={16} style={{ flexShrink: 0 }} />
                <div>
                  <h4 style={{ margin: 0, fontSize: '.875rem', fontWeight: 600 }}>Ficha Incompleta para Geração</h4>
                  <p style={{ margin: 0, fontSize: '.775rem', marginTop: '.25rem' }}>
                    Falta preencher o seguinte para liberar a geração:
                  </p>
                </div>
              </div>

              <div style={{ padding: '.5rem .75rem', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', background: 'var(--bg-card)' }}>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '.8rem', display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
                  {(checkData.missing_fields?.length
                    ? checkData.missing_fields
                    : checkData.missing_groups.map(g => GROUP_LABELS[g] || g)
                  ).map(item => (
                    <li key={item} style={{ color: 'var(--error)', fontWeight: 500 }}>{item}</li>
                  ))}
                </ul>
              </div>

              <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', margin: 0 }}>
                Corrija os dados da ficha antes de tentar gerar a petição.
              </p>
            </div>
          )}

          {checkData && checkData.passed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <p style={{ fontSize: '.875rem', margin: 0, color: 'var(--text-primary)' }}>
                A petição será gerada em segundo plano. Confirme os dados da ficha que serão enviados para a IA:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '.45rem', padding: '.875rem 1rem', background: 'var(--bg)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Cliente</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{checkData.claimant_name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Reclamado</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{checkData.defendant_name || '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Comarca</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{checkData.comarca}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Completude</span>
                  <span style={{ fontWeight: 600, color: 'var(--success)' }}>{checkData.completeness_pct}%</span>
                </div>
                <div style={{ borderTop: '1px solid var(--border-light)', margin: '.25rem 0' }} />
                <div style={{ fontSize: '.8rem' }}>
                  <span style={{ color: 'var(--text-muted)', display: 'block', marginBottom: '.2rem' }}>Pedidos Marcados</span>
                  <span style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: '.775rem', lineHeight: 1.3 }}>
                    {checkData.pedidos_marcados && checkData.pedidos_marcados.length > 0 ? checkData.pedidos_marcados.join(', ') : 'Nenhum pedido selecionado.'}
                  </span>
                </div>
                <div style={{ borderTop: '1px solid var(--border-light)', margin: '.25rem 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.8rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Custo Médio Estimado (IA)</span>
                  <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                    {formatBRL(checkData.estimated_cost_brl ?? 0)}
                  </span>
                </div>
              </div>

              {/* Saldo de IA estimado (1.23): aviso, nunca trava — inclusive quando o saldo é desconhecido. */}
              {checkData.credito?.mensagem && (
                <div className={`alert ${checkData.credito.alerta ? 'alert-warning' : 'alert-info'} aviso-credito`} role="status">
                  <AlertTriangle size={13} />
                  <span>{checkData.credito.mensagem}</span>
                </div>
              )}

              {checkData.estimated_cost_brl && checkData.estimated_cost_brl > 5.0 && (
                <div className="alert alert-warning" style={{ margin: 0, padding: '.5rem .75rem', fontSize: '.75rem' }}>
                  <AlertTriangle size={13} style={{ flexShrink: 0 }} />
                  <span>Aviso: O custo estimado de tokens para este caso supera R$ 5,00.</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            {checkData && !checkData.passed ? 'Voltar e corrigir' : 'Cancelar'}
          </button>

          {checkData && checkData.passed && (
            <button
              className="btn btn-primary btn-sm"
              style={{ background: 'var(--navy)', borderColor: 'var(--navy)' }}
              onClick={onConfirm}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="spinner" style={{ width: 12, height: 12, borderTopColor: '#fff' }} />
                  Iniciando...
                </>
              ) : (
                <>
                  <Zap size={13} /> Confirmar Geração
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
